import values from 'lodash/values';
import memoizeOne from 'memoize-one';
import { getToday } from '@atlassian/jira-software-deployments-common-table/src/utils';
import {
	endOfDay,
	roundForwardToExcludeBadDeploymentsData,
	startOfDay,
	subMonths,
	subWeeks,
	subYears,
	addDays,
} from '../../common/utils/dates';
import type { TimeWindowFilter } from '../../services/core-data/types';
import {
	useDateFilter,
	RelativeDateFilterOptions,
	type DateFilter,
	type CustomDateRange,
	type RelativeDateFilter,
	type RelativeDateFilterOption,
} from './index';

export const isRelativeDate = (dateString: string): boolean =>
	// @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"now" | "-2w" | "-1m" | "-3m" | "-1y"'.
	Object.values(RelativeDateFilterOptions).includes(dateString);

export const toRelativeDate = (relativeDateString: string): RelativeDateFilterOption | undefined =>
	values(RelativeDateFilterOptions).find((relativeDate) => relativeDate === relativeDateString);

export const ONE_YEAR_MAX_IN_MS = 365 * 24 * 3600 * 1000 - 1;
// JSWDDP doesn't accept any time range greater than 365 days, in case of leap year, we will have to make sure the range doesn't exceed 365 days.
export const handleMaxOneYear = ({ after, before }: TimeWindowFilter): TimeWindowFilter => ({
	before,
	after: new Date(Math.max(before.getTime() - ONE_YEAR_MAX_IN_MS, after.getTime())),
});

export const getRelativeDate = (dateFilter: RelativeDateFilterOption): Date => {
	switch (dateFilter) {
		case '-2w':
			return subWeeks(getToday(), 2);
		case '-1m':
			return subMonths(getToday(), 1);
		case '-3m':
			return subMonths(getToday(), 3);
		case '-1y':
			return subYears(addDays(getToday(), 1), 1);
		case 'now':
			return getToday();
		default:
			// prettier-ignore
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			(dateFilter as never);
			throw new Error('No date filter option found when getting relative date');
	}
};

export const getRelativeTimeWindowFilter = (dateFilter: RelativeDateFilter): TimeWindowFilter =>
	handleMaxOneYear({
		after: roundForwardToExcludeBadDeploymentsData(
			startOfDay(getRelativeDate(dateFilter.startDate)),
		),
		before: endOfDay(getRelativeDate(dateFilter.endDate)),
	});

export const getCustomTimeWindowFilter = ({
	startDate,
	endDate,
}: CustomDateRange): TimeWindowFilter => ({
	after: startOfDay(startDate),
	before: endOfDay(endDate),
});

export const getTimeWindowFilter = memoizeOne(
	(dateFilter: DateFilter): TimeWindowFilter =>
		dateFilter.type === 'RELATIVE'
			? getRelativeTimeWindowFilter(dateFilter)
			: getCustomTimeWindowFilter({
					startDate: dateFilter.startDate,
					endDate: dateFilter.endDate,
				}),
);

export const useTimeWindowFilter = (): TimeWindowFilter => {
	const [dateFilter] = useDateFilter();
	return getTimeWindowFilter(dateFilter);
};
